import { useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { getEvent, rsvpEvent, formatApiErrorDetail } from "@/lib/api";
import { track, getUTM } from "@/lib/analytics";
import { Seo } from "@/components/Seo";
import { SITE_URL, DEFAULT_OG_IMAGE, breadcrumbLd } from "@/lib/seo";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Calendar, Clock, MapPin, Users, ArrowLeft, ArrowRight } from "lucide-react";
import { amp } from "@/lib/amp";

const fade = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
};

const labelClass = "text-[11px] uppercase tracking-[0.14em] font-normal text-[color:var(--grey)]";
const fieldClass =
  "mt-1.5 w-full rounded-[2px] border border-[color:var(--rule)] bg-transparent px-4 py-2.5 exec-serif text-[16px] text-[color:var(--ink)] placeholder:text-[color:var(--grey)] focus:outline-none focus:border-[color:var(--copper)] transition-colors";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("en-US", {
      weekday: "long", month: "long", day: "numeric", year: "numeric",
    });
  } catch (e) {
    return iso;
  }
}

function fmtPrice(n) {
  if (n == null) return "";
  return `$${Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

export default function EventDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { data: event, isLoading, isError } = useQuery({
    queryKey: ["event", slug],
    queryFn: () => getEvent(slug),
    retry: false,
  });

  const tickets = useMemo(() => {
    if (!event) return [];
    if (event.tickets && event.tickets.length) return event.tickets;
    return [{ id: "default", name: event.price_label || "Seat", price: null, description: "" }];
  }, [event]);

  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", note: "" });
  const [ticketId, setTicketId] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const selectedTicket = tickets.find((t) => t.id === (ticketId || tickets[0]?.id)) || tickets[0];

  const remaining = event?.seats_remaining ?? 0;
  const isFree = !!event?.free;
  const isOpen = event?.status === "open" && remaining > 0;
  const registered = (event?.seats_total || 0) - remaining;
  const toThreshold = Math.max((event?.activation_threshold || 0) - registered, 0);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim()) {
      setError("Your name and email are required to hold a seat.");
      return;
    }
    setBusy(true);
    try {
      const data = await rsvpEvent(slug, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        company: form.company,
        note: form.note,
        ticket_id: isFree ? null : (selectedTicket?.id === "default" ? null : selectedTicket?.id),
        origin_url: window.location.origin,
        utm: getUTM(),
      });
      track("door_click", { target: "event_rsvp", event: slug, ticket: isFree ? "free" : selectedTicket?.id });
      if (data.url) {
        window.location.href = data.url;
      } else if (data.registered) {
        navigate(`/checkout/success?free=1&event=${encodeURIComponent(slug)}`);
      } else {
        setBusy(false);
        setError("Could not complete your registration. Please try again.");
      }
    } catch (err) {
      setBusy(false);
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Could not start your registration.");
    }
  };

  if (isLoading) {
    return (
      <div className="exec-scope min-h-screen">
        <div className="exec-container pt-24 md:pt-28">
          <div className="h-8 w-40 bg-[color:var(--rule)] animate-pulse" />
          <div className="h-12 w-2/3 bg-[color:var(--rule)] mt-6 animate-pulse" />
          <div className="h-40 w-full bg-[color:var(--rule)] mt-8 animate-pulse" />
        </div>
      </div>
    );
  }

  if (isError || !event) {
    return (
      <div className="exec-scope min-h-screen">
        <div className="exec-container pt-24 md:pt-28 pb-24 max-w-2xl" data-testid="event-detail-notfound">
          <p className="exec-eyebrow">Events</p>
          <h1 className="font-light text-[clamp(26px,3.2vw,38px)] leading-[1.14] mt-6">This room could not be found.</h1>
          <p className="exec-serif text-[17px] text-[color:var(--lead-ink)] mt-4">It may have closed or moved to a new date.</p>
          <Link to="/events" className="exec-link mt-6">
            <ArrowLeft size={14} /> Back to all events
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="exec-scope min-h-screen" data-testid="event-detail-page">
      {event && (
        <Seo
          title={`${event.title} — Copper & Vine`}
          description={event.description ? String(event.description).slice(0, 155) : `${event.title}. An operator event from Copper & Vine.`}
          path={`/events/${slug}`}
          type="article"
          jsonLd={[
            {
              "@context": "https://schema.org",
              "@type": "Event",
              name: event.title,
              startDate: event.event_date || undefined,
              eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
              eventStatus: "https://schema.org/EventScheduled",
              location: { "@type": "Place", name: event.location || "Private", address: event.location || "" },
              description: event.description || undefined,
              image: DEFAULT_OG_IMAGE,
              organizer: { "@type": "Organization", name: "Copper & Vine", url: SITE_URL },
              offers: {
                "@type": "Offer",
                price: isFree ? 0 : (selectedTicket?.price ?? 0),
                priceCurrency: "USD",
                availability: isOpen ? "https://schema.org/InStock" : "https://schema.org/SoldOut",
                url: `${SITE_URL}/events/${slug}`,
              },
            },
            breadcrumbLd([
              { name: "Home", path: "/" },
              { name: "Events", path: "/events" },
              { name: event.title, path: `/events/${slug}` },
            ]),
          ]}
        />
      )}
      <section className="exec-container pt-24 md:pt-28 pb-20">
        <Link to="/events" className="exec-link" data-testid="event-back-link">
          <ArrowLeft size={14} /> All events
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-10 lg:gap-14 mt-8">
          {/* Left: editorial event details */}
          <motion.div {...fade}>
            <span className="exec-eyebrow">{amp(event.kind)}</span>
            <h1 data-testid="event-title" className="font-extralight text-[clamp(30px,4.4vw,52px)] leading-[1.1] mt-4">{event.title}</h1>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="flex items-start gap-3">
                <Calendar size={18} className="text-[color:var(--copper-deep)] mt-0.5 shrink-0" />
                <div>
                  <p className={labelClass}>Date</p>
                  <p data-testid="event-date" className="exec-serif text-[16px] text-[color:var(--ink)] mt-1">{fmtDate(event.event_date)}</p>
                </div>
              </div>
              {event.event_time && (
                <div className="flex items-start gap-3">
                  <Clock size={18} className="text-[color:var(--copper-deep)] mt-0.5 shrink-0" />
                  <div>
                    <p className={labelClass}>Time</p>
                    <p data-testid="event-time" className="exec-serif text-[16px] text-[color:var(--ink)] mt-1">{event.event_time}</p>
                  </div>
                </div>
              )}
              <div className="flex items-start gap-3">
                <MapPin size={18} className="text-[color:var(--copper-deep)] mt-0.5 shrink-0" />
                <div>
                  <p className={labelClass}>Location</p>
                  <p data-testid="event-location" className="exec-serif text-[16px] text-[color:var(--ink)] mt-1">{event.location || event.format}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Users size={18} className="text-[color:var(--copper-deep)] mt-0.5 shrink-0" />
                <div>
                  <p className={labelClass}>Seats</p>
                  <p className="exec-serif text-[16px] text-[color:var(--ink)] mt-1">{remaining} of {event.seats_total} left</p>
                </div>
              </div>
            </div>

            <p className="exec-serif text-[clamp(17px,1.9vw,20px)] leading-[1.55] text-[color:var(--lead-ink)] mt-8 max-w-2xl">{event.description}</p>

            {(event.who_for || event.what_leave || event.why_now) && (
              <dl className="mt-6 space-y-4 max-w-2xl">
                {event.who_for && (
                  <div className="exec-serif text-[16px]">
                    <dt className="text-[11px] uppercase tracking-[0.16em] font-normal text-[color:var(--copper-deep)]">Who this is for</dt>
                    <dd className="text-[color:var(--lead-ink)] mt-1.5">{event.who_for}</dd>
                  </div>
                )}
                {event.what_leave && (
                  <div className="exec-serif text-[16px]">
                    <dt className="text-[11px] uppercase tracking-[0.16em] font-normal text-[color:var(--copper-deep)]">What you will leave with</dt>
                    <dd className="text-[color:var(--lead-ink)] mt-1.5">{event.what_leave}</dd>
                  </div>
                )}
                {event.why_now && (
                  <div className="exec-serif text-[16px]">
                    <dt className="text-[11px] uppercase tracking-[0.16em] font-normal text-[color:var(--copper-deep)]">Why now</dt>
                    <dd className="text-[color:var(--lead-ink)] mt-1.5">{event.why_now}</dd>
                  </div>
                )}
              </dl>
            )}
          </motion.div>

          {/* Right: RSVP panel (contact details + ticket -> payment) */}
          <motion.div {...fade} className="lg:sticky lg:top-28 h-fit">
            <div data-testid="event-rsvp-panel" className="exec-card overflow-hidden">
              <div className="p-7 sm:p-8">
                <p className="exec-eyebrow">Register</p>
                <p className="font-light text-[clamp(22px,2.4vw,28px)] leading-[1.18] mt-2">Hold your seat.</p>
                <p className="exec-serif text-[15.5px] leading-[1.5] text-[color:var(--lead-ink)] mt-3">
                  {isFree
                    ? "A seat is held when you RSVP. No payment required. Your email is verified to confirm your place."
                    : "A seat is held when you register. Your email is verified before payment. Checkout by Stripe."}
                </p>

                {!isOpen ? (
                  <div data-testid="event-closed-notice" className="mt-6 border-l-2 border-[color:var(--copper)] pl-4">
                    <p className="exec-serif text-[16px] text-[color:var(--ink)]">
                      {remaining <= 0 ? "This room is full." : "Registration is closed for this room."}
                    </p>
                    <Link to="/events" className="exec-link mt-3">
                      See other rooms <ArrowRight size={14} />
                    </Link>
                  </div>
                ) : (
                  <form onSubmit={submit} className="mt-6 space-y-4" data-testid="event-rsvp-form">
                    {/* Ticket type (paid events only) */}
                    {!isFree && (
                    <label className="block">
                      <span className={labelClass}>Ticket type</span>
                      <div className="mt-1.5">
                        <Select value={selectedTicket?.id} onValueChange={setTicketId}>
                          <SelectTrigger data-testid="event-ticket-select" className="w-full bg-transparent border-[color:var(--rule)] rounded-[2px] text-[color:var(--ink)]">
                            <SelectValue placeholder="Select a ticket" />
                          </SelectTrigger>
                          <SelectContent>
                            {tickets.map((t) => (
                              <SelectItem key={t.id} value={t.id} data-testid={`event-ticket-option-${t.id}`}>
                                {t.name}{t.price != null ? ` · ${fmtPrice(t.price)}` : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {selectedTicket?.description && (
                        <span className="exec-serif text-[14px] text-[color:var(--grey)] mt-2 block">{selectedTicket.description}</span>
                      )}
                    </label>
                    )}

                    {/* Contact details */}
                    <label className="block">
                      <span className={labelClass}>Full name</span>
                      <input data-testid="rsvp-name" className={fieldClass} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                    </label>
                    <label className="block">
                      <span className={labelClass}>Email</span>
                      <input data-testid="rsvp-email" type="email" className={fieldClass} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <label className="block">
                        <span className={labelClass}>Phone</span>
                        <input data-testid="rsvp-phone" className={fieldClass} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                      </label>
                      <label className="block">
                        <span className={labelClass}>Company</span>
                        <input data-testid="rsvp-company" className={fieldClass} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
                      </label>
                    </div>
                    <label className="block">
                      <span className={labelClass}>What do you want from this room?</span>
                      <textarea data-testid="rsvp-note" rows={3} className={`${fieldClass} resize-none`} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Optional. One line on the decision you are carrying." />
                    </label>

                    {error && <p data-testid="rsvp-error" className="exec-serif text-[16px] text-[color:var(--ink)] border-l-2 border-[color:var(--copper)] pl-3">{error}</p>}

                    <button data-testid="rsvp-submit" type="submit" disabled={busy} className="exec-btn exec-btn-primary w-full disabled:opacity-60">
                      {busy
                        ? (isFree ? "Reserving\u2026" : "Opening checkout\u2026")
                        : (isFree
                            ? "Reserve your seat, free"
                            : `Continue to payment${selectedTicket?.price != null ? ` · ${fmtPrice(selectedTicket.price)}` : ""}`)}
                    </button>

                    {toThreshold > 0 && (
                      <p className="exec-serif text-[14px] text-[color:var(--grey)]">
                        {isFree
                          ? `Confirms when ${event.activation_threshold} people register.${event.activation_date ? ` Final headcount set by ${fmtDate(event.activation_date)}.` : ""}`
                          : `Confirms when ${event.activation_threshold} people register.${event.activation_date ? ` If it does not reach ${event.activation_threshold} by ${fmtDate(event.activation_date)}, your card is refunded automatically.` : ""}`}
                      </p>
                    )}
                  </form>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
